import type { ReactNode } from 'react';
import { Inbox, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn.js';
import type { ListMeta } from '@/lib/api-client.js';
import { Button } from './Button.js';

export type Column<T> = {
  /** Ustun kaliti — React `key` sifatida ham ishlatiladi. */
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Raqamli ustunlar o'ngga tekislanadi va `tabular` bo'ladi. */
  align?: 'left' | 'right';
  width?: string;
  /** Planshetda yashiriladigan ikkinchi darajali ustunlar. */
  hideOnTablet?: boolean;
};

export type DataTableProps<T> = {
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  /** Bo'sh holat matni — har bir bo'limda o'ziga xos (TZ 4.4). */
  emptyTitle?: string;
  emptyHint?: ReactNode;
  meta?: ListMeta;
  onPageChange?: (page: number) => void;
};

/**
 * Admin jadvali.
 *
 * Uchta holat bitta joyda: yuklanish (skeleton), bo'sh va ma'lumotli.
 * Har bir bo'limda qayta yozilsa, birortasida bo'sh holat unutilardi va
 * foydalanuvchi bo'm-bo'sh ekranni ko'rib «buzilibdi» deb o'ylardi.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  onRowClick,
  emptyTitle = 'Hozircha yozuv yo‘q',
  emptyHint,
  meta,
  onPageChange,
}: DataTableProps<T>) {
  return (
    /*
      Maketda jadval sarlavhasi — och kulrang fon va QORA pastki chiziq
      (`D2`, `D3`): u ustunlarni tarkibdan qat'iy ajratadi. Sarlavha
      matni oddiy o'lchamda, katta harflarga o'girilmaydi — uzun
      o'zbekcha ustun nomlari ikki qatorga sinib ketardi.
    */
    <div className="overflow-hidden border border-n-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead className="border-b border-n-900 bg-n-50 text-left font-semibold text-n-900">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    'px-3.5 py-3 whitespace-nowrap',
                    column.align === 'right' && 'text-right',
                    column.hideOnTablet && 'hidden lg:table-cell',
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-n-100">
            {loading && <SkeletonRows columns={columns} />}

            {!loading &&
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn('transition-colors', onRowClick && 'cursor-pointer hover:bg-n-50')}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        'px-3.5 py-3 align-middle text-n-800',
                        column.align === 'right' && 'font-mono tabular text-right',
                        column.hideOnTablet && 'hidden lg:table-cell',
                      )}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && rows.length === 0 && <EmptyState title={emptyTitle} hint={emptyHint} />}

      {meta && meta.totalPages > 1 && onPageChange && (
        <Pagination meta={meta} onPageChange={onPageChange} />
      )}
    </div>
  );
}

function SkeletonRows<T>({ columns }: { columns: ReadonlyArray<Column<T>> }) {
  return (
    <>
      {Array.from({ length: 5 }, (_, rowIndex) => (
        <tr key={rowIndex} aria-hidden>
          {columns.map((column) => (
            <td key={column.key} className="px-4 py-3">
              <div className="h-4 animate-pulse bg-n-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <Inbox className="size-8 text-n-200" aria-hidden />
      <p className="font-medium text-n-800">{title}</p>
      {hint && <div className="max-w-md text-sm text-n-600">{hint}</div>}
    </div>
  );
}

function Pagination({
  meta,
  onPageChange,
}: {
  meta: ListMeta;
  onPageChange: (page: number) => void;
}) {
  const from = (meta.page - 1) * meta.limit + 1;
  const to = Math.min(meta.page * meta.limit, meta.total);

  return (
    <nav
      aria-label="Sahifalar"
      className="flex items-center justify-between border-t border-n-200 px-4 py-3 text-sm"
    >
      <p className="text-n-600">
        <span className="tabular">
          {from}–{to}
        </span>{' '}
        / jami <span className="tabular">{meta.total}</span>
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={meta.page <= 1}
          onClick={() => onPageChange(meta.page - 1)}
        >
          Oldingi
        </Button>
        <span className="tabular text-n-600">
          {meta.page} / {meta.totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={meta.page >= meta.totalPages}
          onClick={() => onPageChange(meta.page + 1)}
        >
          Keyingi
        </Button>
      </div>
    </nav>
  );
}

export function InlineSpinner({ label = 'Yuklanmoqda' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-n-600">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </span>
  );
}
